// components/ExportPdfButton.jsx
import { useRef, useState } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { Download } from "react-feather";
import axios from "axios";
import styles from "../creditreport.module.css";
import PrintableReport from "./PrintableReport";

const ExportPdfButton = ({
    getAccessTokenSilently,
    propertyName,
    dateRangeLabel,
    kpiData,
    chartData,
    tierList,
    goals,
    mapResidentToRow,
}) => {
    const [exporting, setExporting] = useState(false);
    const [printResidents, setPrintResidents] = useState(null);
    const printRef = useRef(null);

    const waitForImages = (container) => {
        const images = Array.from(container.querySelectorAll("img"));
        return Promise.all(
            images.map((img) => {
                if (img.complete) return Promise.resolve();
                return new Promise((resolve) => {
                    img.onload = resolve;
                    img.onerror = resolve;
                });
            })
        );
    };

    const handleExport = async () => {
        if (exporting) return;
        setExporting(true);

        try {
            const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

            const response = await axios(process.env.REACT_APP_TENANT_API_URL + "v1/credit-builder/tenant-list", {
                headers: { Authorization: `Bearer ${token}` },
                params: { per_page: 1000 },
            });

            const paginator = response.data.data;
            const rows = Array.isArray(paginator) ? paginator : (paginator?.data || []);
            setPrintResidents(rows.map(mapResidentToRow));

            // REQUIRED: setPrintResidents() above doesn't render synchronously.
            // <PrintableReport> only mounts once printResidents is truthy, and printRef.current
            // stays null until that mount happens. Without this wait, the check below
            // always fails on the very first attempt.
            let attempts = 0;
            while (!printRef.current && attempts < 40) {
                await new Promise((resolve) => setTimeout(resolve, 50));
                attempts++;
            }

            if (!printRef.current) {
                console.error("PrintableReport did not mount in time");
                return;
            }

            await waitForImages(printRef.current);

            const pages = printRef.current.querySelectorAll(".printPage");
            const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();

            for (let i = 0; i < pages.length; i++) {
                const pageEl = pages[i];
                const canvas = await html2canvas(pageEl, {
                    scale: 2,
                    useCORS: true,
                    allowTaint: false,
                    backgroundColor: "#ffffff",
                });
                const imgData = canvas.toDataURL("image/png");
                if (i > 0) {
                    pdf.addPage();
                }
                pdf.addImage(imgData, "PNG", 0, 0, pageWidth, pageHeight);
            }

            pdf.save(`credit-to-wealth-report-${new Date().toISOString().slice(0, 10)}.pdf`);
        } catch (err) {
            console.error("PDF export failed", err);
        } finally {
            setExporting(false);
            setPrintResidents(null);
        }
    };

    return (
        <>
            <button className={styles.exportBtn} onClick={handleExport} disabled={exporting}>
                <Download size={14} style={{ marginRight: "6px" }} />
                {exporting ? "Exporting..." : "Export PDF"}
            </button>

            {printResidents && (
                <div style={{ position: "absolute", left: "-9999px", top: 0, width: "800px" }}>
                    <PrintableReport
                        ref={printRef}
                        propertyName={propertyName}
                        dateRangeLabel={dateRangeLabel}
                        kpiData={kpiData}
                        chartData={chartData}
                        tierList={tierList}
                        goals={goals}
                        residents={printResidents}
                    />
                </div>
            )}
        </>
    );
};

export default ExportPdfButton;